import { parseMagnitudeNumber } from "../../core/parseMagnitude";

// GROUNDING + action vocabulary for the reusable `table_view` capability — the affordances of the shared
// data-grid (`common/components/Table`) that appears on the Recommendations page and every future table
// page: sort, hide/show columns, pin, page size, the resets, and export. Otto's brain (the backend) only
// CAPTURES which action + which column (as a phrase) + any value; the real, config/permission-scoped column
// set and the execution live on the frontend — exactly like the filter contract and the navigation catalog
// (`navCatalog.js`, whose grounding this mirrors). A page registers a `tableController` (its own bound
// handlers) and sends its LIVE table state as `app_context.table`; this module grounds the captured phrase
// against that real state before anything is proposed. Nothing here is invented per page — the columns come
// from the page's own `tableData.config`, so when a page's columns change, the grounding follows.

// --- action vocabulary -------------------------------------------------------
// The canonical set the interpreter captures. Kept HERE (the surface), so the AI layer hardcodes no page
// specifics — the backend `TableCommand` action enum mirrors these keys (same "source affordances from the
// surface" rule the charts capability follows). Flags drive grounding + UX:
//   needsColumn — the action targets a column, so the phrase must GROUND to a real one before proposing.
//   needsValue  — the action carries a value (page size / export format / sort direction) to validate.
//   autoApply   — cheap + reversible + instantly visible → apply on confidence; false → confirm first
//                 (resets are destructive-ish; export produces an artifact). Matches the plan's UX matrix.
export const TABLE_ACTIONS = {
    sort: { needsColumn: true, needsValue: false, autoApply: true }, // direction optional (toggles asc/desc)
    hide_column: { needsColumn: true, needsValue: false, autoApply: true },
    show_column: { needsColumn: true, needsValue: false, autoApply: true },
    pin_column: { needsColumn: true, needsValue: false, autoApply: true },
    unpin_column: { needsColumn: true, needsValue: false, autoApply: true },
    unpin_all: { needsColumn: false, needsValue: false, autoApply: true },
    set_page_size: { needsColumn: false, needsValue: true, autoApply: true },
    reset_sort: { needsColumn: false, needsValue: false, autoApply: false },
    reset_filters: { needsColumn: false, needsValue: false, autoApply: false }, // the page's LOCAL filters
    reset_table: { needsColumn: false, needsValue: false, autoApply: false }, // column config → project default
    export: { needsColumn: false, needsValue: true, autoApply: false },
    // Row filtering on grids that filter in place (no separate panel — e.g. Buildings): narrow WHICH rows
    // show by a column value. Grounded (option value or numeric/text threshold) then CONFIRM — it changes
    // which data is shown, so never auto-applied. `clear_column_filter` removes one column's row-filter.
    column_filter: { needsColumn: true, needsValue: true, autoApply: false },
    clear_column_filter: { needsColumn: true, needsValue: false, autoApply: false },
    // ---- VIEW-SHAPING actions: change what is SHOWN, never what is stored. Each is declared by the page via
    // an optional tableController dep; a page that wired nothing simply never offers them (see
    // controller.availableActions() / buildTableContext's availableActions below), rather than offering an
    // action that silently no-ops.
    search: { needsColumn: false, needsValue: true, autoApply: false }, // narrows which rows show → confirm
    clear_search: { needsColumn: false, needsValue: false, autoApply: true },
    sort_recent: { needsColumn: false, needsValue: true, autoApply: true }, // "created" | "edited"
    goto_column: { needsColumn: true, needsValue: false, autoApply: true }, // scroll + highlight only
    reset_all: { needsColumn: false, needsValue: false, autoApply: false }, // filters AND sort → confirm
    open_filters: { needsColumn: false, needsValue: false, autoApply: true },
    open_applied_filters: { needsColumn: false, needsValue: false, autoApply: true }
};

export const isTableAction = a => Object.prototype.hasOwnProperty.call(TABLE_ACTIONS, a);

// The two "recently touched" sort axes the shared grid exposes as their own controls (Sort by Newly Created /
// Newly Edited). NOT columns in tableData.config — that is why sort_recent is a separate action from `sort`.
export const RECENT_SORT_AXES = ["created", "edited"];

// The row-count options the shared table offers (RecommendationsMain's per-page <select>); a captured page
// size is grounded to the nearest supported value by the proposal layer. Default is 40.
export const PAGE_SIZE_OPTIONS = [10, 20, 30, 40, 50, 100, 150];

// Export targets the interpreter may capture. This is the CAPTURE vocabulary, not what any given page can
// actually produce — a page declares its REAL formats via its tableController (see EXPORT_CAPABILITY_DEFAULT
// / exportOptionsOf). The proposal grounds a captured format against the page's own list, so Otto never
// promises a Word export on a page that only builds Excel.
export const EXPORT_FORMATS = ["excel", "word", "pdf"];

// The two export MODES a table page can offer (sourced from the pages' own export UI):
//   simple   — one click, one file, no settings (Buildings' Excel icon; Recommendations' "Simple Excel Export").
//   advanced — the page's own export CHOOSER, where the user picks the variant + its settings themselves
//              (Recommendations' ExportPopup: Simple Excel / Normal Grouping / Criticality-Based, each with
//              its "Advanced Export Settings" modal). Otto opens the chooser rather than guessing a variant.
// A page with `advanced:false` has only the simple export, so Otto never offers a choice there.
export const EXPORT_MODES = ["simple", "advanced"];

// What a table page supports when it registers `exportTable` but declares nothing else: a simple Excel
// download and no advanced chooser. Deliberately the narrowest truthful claim.
export const EXPORT_CAPABILITY_DEFAULT = { enabled: true, formats: ["excel"], advanced: false };

// A page's NAMED export variants — the entries of its own export chooser, declared so Otto can offer and
// RUN them individually instead of collapsing everything beyond the simple export into one "Advanced
// Export" hand-off (Recommendations: Normal Grouping / CrossWalk / Criticality-Based — see ExportPopup.jsx,
// the source of truth these declarations mirror). Each descriptor:
//   key        — stable id the page's `exportVariant(key, opts)` dispatches on
//   label      — the chooser's own card title, so Otto and the popup speak the same names
//   formats    — file types THIS variant builds (chooser radios); defaults to ["excel"]
//   breakdowns — the variant's own axis, if any (Criticality-Based: building/addition/responsibility);
//                first entry = the chooser's default radio
//   synonyms   — normalized phrases that name the variant, for utterance grounding (like nav synonyms)
// Normalization is defensive AND idempotent (its own output round-trips unchanged) — exportOptionsOf runs
// once controller-side and again proposal-side on the same payload.
export const exportVariantsOf = declared => {
    const list = Array.isArray(declared) ? declared : [];
    return list
        .filter(v => v && v.key && v.label)
        .map(v => {
            const formats = (Array.isArray(v.formats) ? v.formats : [])
                .map(f => String(f || "").toLowerCase())
                .filter(f => EXPORT_FORMATS.includes(f));
            return {
                key: String(v.key),
                label: String(v.label),
                formats: formats.length ? formats : ["excel"],
                breakdowns: (Array.isArray(v.breakdowns) ? v.breakdowns : []).map(b => String(b || "").toLowerCase().trim()).filter(Boolean),
                synonyms: (Array.isArray(v.synonyms) ? v.synonyms : []).map(s => String(s || "")).filter(Boolean)
            };
        });
};

// Normalize a page's declared export capability into { enabled, formats, advanced, variants }. Keeps only
// formats Otto knows how to name, and falls back to the default rather than trusting a malformed declaration
// — the same defensive posture as buildTableColumns. `enabled:false` means this page offers the user no
// export at all (e.g. the export permission is off), so Otto says so instead of offering a button that can't
// work. `formats` remains what the SIMPLE export builds; variant formats live on each variant.
export const exportOptionsOf = declared => {
    const d = declared || {};
    const formats = (Array.isArray(d.formats) ? d.formats : [])
        .map(f => String(f || "").toLowerCase())
        .filter(f => EXPORT_FORMATS.includes(f));
    return {
        enabled: d.enabled !== false,
        formats: formats.length ? formats : EXPORT_CAPABILITY_DEFAULT.formats,
        advanced: !!d.advanced,
        variants: exportVariantsOf(d.variants)
    };
};

// Phrases that pin the user to ONE mode, so Otto skips the choice when they already said which they want.
// Deterministic frontend detection (like pageChoice) — no interpreter prompt change, so no eval churn.
const ADVANCED_PHRASES = [
    "advanced",
    "export settings",
    "settings",
    "grouping",
    "criticality",
    "crosswalk",
    "cross walk",
    "with options",
    "more options",
    "configure",
    "customize",
    "customise"
];
const SIMPLE_PHRASES = ["simple", "plain", "regular", "basic", "quick", "just the table", "straight"];

// Which export MODE the user asked for, from their own words. Returns "simple" | "advanced" | null (null =
// they didn't say, so the page offers the choice when it has both). NOTE: several ADVANCED_PHRASES double as
// variant names ("grouping", "criticality", "crosswalk") — on a page that DECLARES variants, variant
// grounding runs first and those words reach their variant; only on variant-less pages (or a gated-off
// variant) do they still fall through to the chooser.
export const detectExportMode = utterance => {
    const norm = ` ${normalizeText(utterance)} `;
    if (!norm.trim()) return null;
    const hasAny = phrases => phrases.some(p => norm.includes(` ${normalizeText(p)} `));
    if (hasAny(ADVANCED_PHRASES)) return "advanced";
    if (hasAny(SIMPLE_PHRASES)) return "simple";
    return null;
};

// Which declared export VARIANTS the utterance names (label + synonyms, whole words — the same rule nav
// grounding and pagesNamedIn use). [] = none named; the proposal then offers the full choice.
export const groundExportVariants = (utterance, variants) => {
    const norm = ` ${normalizeText(utterance)} `;
    if (!norm.trim()) return [];
    return (variants || []).filter(v =>
        [v.label, ...(v.synonyms || [])]
            .map(normalizeText)
            .filter(Boolean)
            .some(n => norm.includes(` ${n} `))
    );
};

// Which of a variant's BREAKDOWNS the utterance names ("by building", "per responsibility"). Requires the
// by/per preposition so a breakdown word appearing as ordinary vocabulary ("the building exports") doesn't
// silently pick an axis. null = none named (the proposal uses the variant's default — its first entry).
export const groundExportBreakdown = (utterance, variant) => {
    const norm = ` ${normalizeText(utterance)} `;
    const hits = (variant && Array.isArray(variant.breakdowns) ? variant.breakdowns : []).filter(b => {
        const n = normalizeText(b);
        return n && (norm.includes(` by ${n} `) || norm.includes(` per ${n} `));
    });
    return hits.length === 1 ? hits[0] : null;
};

// A FORMAT named in the user's own words ("as word"), for when the interpreter didn't capture one. Whole
// words, first match in the canonical order. null = not mentioned.
export const detectExportFormat = utterance => {
    const norm = ` ${normalizeText(utterance)} `;
    if (!norm.trim()) return null;
    return EXPORT_FORMATS.find(f => norm.includes(` ${f} `)) || null;
};

// "…including the project cost total" — the export popup's one global toggle (`includeSoftCost`, default
// OFF). Deterministic opt-in from the user's words, matching the popup's default when unsaid.
const SOFT_COST_PHRASES = ["project cost total", "project cost totals", "project cost", "soft cost", "soft costs"];
export const detectIncludeSoftCost = utterance => {
    const norm = ` ${normalizeText(utterance)} `;
    if (!norm.trim()) return false;
    return SOFT_COST_PHRASES.some(p => norm.includes(` ${normalizeText(p)} `));
};

// GROUND a captured export format against what THIS page can actually build. Returns:
//   { decision: "auto",     format }            — the page supports it (or only builds one format anyway)
//   { decision: "advanced" }                    — the page can't build it simply, but its advanced chooser can
//   { decision: "clarify",  formats }           — the page builds several and we don't know which was meant
// A page that builds exactly one format never clarifies: there is nothing to choose.
export const groundExportFormat = (captured, options) => {
    const opts = exportOptionsOf(options);
    const fmt = String(captured || "").toLowerCase();
    if (opts.formats.includes(fmt)) return { decision: "auto", format: fmt };
    // A real format the page can't build simply. Its advanced chooser may still offer it (Recommendations
    // builds Word only inside the export chooser), so send them there instead of refusing.
    if (EXPORT_FORMATS.includes(fmt) && opts.advanced) return { decision: "advanced" };
    if (opts.formats.length === 1) return { decision: "auto", format: opts.formats[0] };
    return { decision: "clarify", formats: opts.formats };
};

// Normalize text for matching: lowercase, strip punctuation, collapse whitespace. (Same as navCatalog's —
// kept local so the capability stays self-contained rather than cross-importing another capability.)
export const normalizeText = s =>
    String(s == null ? "" : s)
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, " ")
        .trim();

// Normalize the page's live table state (`app_context.table`, or a controller's getContext()) into column
// grounding candidates. Defaults `isVisible` to true (an omitted flag means shown) and keeps `pinned`/`type`
// for the proposal layer to reason about (e.g. "hide" only targets visible columns, "show" only hidden).
export const buildTableColumns = tableCtx => {
    const cols = (tableCtx && tableCtx.columns) || [];
    return cols
        .filter(c => c && (c.key != null || c.label != null))
        .map(c => ({
            key: c.key != null ? c.key : c.label,
            label: c.label != null ? c.label : String(c.key),
            isVisible: c.isVisible !== false,
            pinned: !!c.pinned,
            type: c.type || null,
            // How this column can be ROW-filtered (column_filter): "options" = a discrete option list
            // (grounded against the column's real values), "wildcard" = a numeric/text threshold, "none" =
            // not filterable. Derived from the grid's own column flags; `filterKind` wins if a page sets it
            // explicitly. Labels only — no searchKeys leak to the backend (option fetch + apply are keyed by
            // the column KEY through the page's tableController).
            filterKind: c.filterKind || (c.hasCommonSearch ? "options" : c.hasWildCardSearch ? "wildcard" : "none"),
            synonyms: Array.isArray(c.synonyms) ? c.synonyms : []
        }));
};

// Score one column against a normalized phrase. 1.0 = exact label/synonym; 0.8 = one contains the other;
// otherwise a token-overlap fraction. 0 = no overlap. (Mirrors navCatalog.scoreTarget for consistent feel.)
const scoreColumn = (phrase, col) => {
    const labels = [normalizeText(col.label), ...(col.synonyms || []).map(normalizeText)].filter(Boolean);
    const phraseTokens = new Set(phrase.split(" ").filter(Boolean));
    let best = 0;
    labels.forEach(label => {
        if (!label) return;
        if (label === phrase) {
            best = Math.max(best, 1);
            return;
        }
        if (phrase.includes(label) || label.includes(phrase)) {
            best = Math.max(best, 0.8);
            return;
        }
        const labelTokens = label.split(" ").filter(Boolean);
        if (!labelTokens.length) return;
        const shared = labelTokens.filter(t => phraseTokens.has(t)).length;
        if (shared) best = Math.max(best, (shared / labelTokens.length) * 0.7);
    });
    return best;
};

// GROUND a column phrase against the page's real columns. Returns:
//   { decision: "auto",    column, candidates } — one confident column (the proposal still confirms/auto per action)
//   { decision: "clarify", candidates }         — several plausible columns, ask which
//   { decision: "none",    candidates: [] }     — nothing matched a real column (→ a clarification, never a wrong action)
// Same tiered logic as navigation's groundDestination so column and page grounding behave identically.
export const groundColumn = (phrase, columns) => {
    const p = normalizeText(phrase);
    if (!p || !columns || !columns.length) return { decision: "none", candidates: [] };
    const scored = columns
        .map(column => ({ column, score: scoreColumn(p, column) }))
        .filter(s => s.score > 0)
        .sort((a, b) => b.score - a.score);
    if (!scored.length) return { decision: "none", candidates: [] };

    const exact = scored.filter(s => s.score >= 0.999);
    if (exact.length === 1) return { decision: "auto", column: exact[0].column, candidates: scored.map(s => s.column) };
    if (exact.length > 1) return { decision: "clarify", candidates: exact.map(s => s.column) };

    const strong = scored.filter(s => s.score >= 0.8);
    if (strong.length === 1) return { decision: "auto", column: strong[0].column, candidates: scored.map(s => s.column) };
    if (strong.length > 1) return { decision: "clarify", candidates: strong.map(s => s.column) };

    const near = scored.filter(s => s.score >= 0.5);
    if (near.length === 1) return { decision: "auto", column: near[0].column, candidates: scored.map(s => s.column) };
    if (near.length > 1) return { decision: "clarify", candidates: near.map(s => s.column) };
    return { decision: "clarify", candidates: scored.slice(0, 4).map(s => s.column) };
};

// ---- value grounding (column_filter) ---------------------------------------
// A column's real option VALUES are fetched by the page (controller.getColumnOptions) and grounded here —
// exactly the "brain captures a phrase, the frontend grounds it against real data" rule the filter/nav
// paths follow. Options are `{ id?, name }`; matching is on the display `name`.
const scoreValue = (phrase, name) => {
    const label = normalizeText(name);
    if (!label) return 0;
    if (label === phrase) return 1;
    if (phrase.includes(label) || label.includes(phrase)) return 0.8;
    const labelTokens = label.split(" ").filter(Boolean);
    const phraseTokens = new Set(phrase.split(" ").filter(Boolean));
    if (!labelTokens.length) return 0;
    const shared = labelTokens.filter(t => phraseTokens.has(t)).length;
    return shared ? (shared / labelTokens.length) * 0.7 : 0;
};

// GROUND one value phrase against a column's real options. Returns the same decision shape as groundColumn:
//   { decision: "auto", value, candidates } | { decision: "clarify", candidates } | { decision: "none", candidates:[] }
export const groundValue = (phrase, options) => {
    const p = normalizeText(phrase);
    if (!p || !Array.isArray(options) || !options.length) return { decision: "none", candidates: [] };
    const scored = options
        .map(opt => ({ opt, score: scoreValue(p, opt && (opt.name != null ? opt.name : opt.id)) }))
        .filter(s => s.score > 0)
        .sort((a, b) => b.score - a.score);
    if (!scored.length) return { decision: "none", candidates: [] };

    const exact = scored.filter(s => s.score >= 0.999);
    if (exact.length === 1) return { decision: "auto", value: exact[0].opt, candidates: scored.map(s => s.opt) };
    if (exact.length > 1) return { decision: "clarify", candidates: exact.map(s => s.opt) };
    const strong = scored.filter(s => s.score >= 0.8);
    if (strong.length === 1) return { decision: "auto", value: strong[0].opt, candidates: scored.map(s => s.opt) };
    if (strong.length > 1) return { decision: "clarify", candidates: strong.map(s => s.opt) };
    const near = scored.filter(s => s.score >= 0.5);
    if (near.length === 1) return { decision: "auto", value: near[0].opt, candidates: scored.map(s => s.opt) };
    return { decision: "clarify", candidates: scored.slice(0, 6).map(s => s.opt) };
};

// ---- numeric / text threshold parsing (column_filter on a wildcard column) --
// Operator ids match the grid's wildcard filters EXACTLY (utils/wildCardFilterUtils.js) so the applied
// filter is indistinguishable from a user-typed one. Each entry lists WORD phrases (matched on word
// boundaries) and SYMBOL tokens (matched as substrings — `\b` doesn't bracket symbols). Most-specific
// first (">=" before ">") so a compound phrase isn't captured by a shorter operator.
const NUMERIC_OPS = [
    { op: "greater_than_or_equal", words: ["at least", "no less than", "greater than or equal to", "minimum of"], symbols: [">=", "≥"] },
    { op: "less_than_or_equal", words: ["at most", "no more than", "less than or equal to", "up to", "maximum of"], symbols: ["<=", "≤"] },
    { op: "greater_than", words: ["greater than", "more than", "over", "above", "exceeding", "exceeds", "bigger than", "larger than"], symbols: [">"] },
    { op: "less_than", words: ["less than", "fewer than", "under", "below", "smaller than"], symbols: ["<"] },
    { op: "equals", words: ["equals", "equal to", "exactly", "equal"], symbols: ["="] }
];
const TEXT_OPS = [
    { op: "exclude", words: ["doesn't contain", "does not contain", "not containing", "excluding", "without"], symbols: [] },
    { op: "begins_with", words: ["starts with", "start with", "begins with", "begin with", "beginning with"], symbols: [] },
    { op: "ends_with", words: ["ends with", "end with", "ending with"], symbols: [] },
    { op: "string_equals", words: ["equals", "equal to", "is exactly", "exactly"], symbols: [] },
    { op: "contains", words: ["contains", "contain", "containing", "includes", "include", "like"], symbols: [] }
];

const isNumericType = t => t === "number" || t === "currency" || t === "year";
const escapeRe = s => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
// True when any of the op's words (as whole words) or symbols (as substrings) appears in `lower`.
const opMatches = (lower, entry) =>
    (entry.symbols || []).some(sym => lower.includes(sym)) ||
    (entry.words || []).some(w => new RegExp(`\\b${escapeRe(w)}\\b`).test(lower));

// True when a value phrase carries an explicit comparison/text operator (so it's a THRESHOLD, not a plain
// option value) — used to route "area over 100000" to a wildcard even on a column that also has options.
export const hasThresholdOperator = phrase => {
    const lower = String(phrase == null ? "" : phrase).toLowerCase();
    if (!lower) return false;
    return NUMERIC_OPS.some(o => opMatches(lower, o)) || TEXT_OPS.some(o => opMatches(lower, o));
};

// Parse a threshold phrase ("over 100000", "at least 50", "contains hospital") for a wildcard column into
// { operator, value } using the grid's operator vocabulary. Returns null when no value can be extracted.
export const parseWildcardValue = (phrase, type) => {
    const raw = String(phrase == null ? "" : phrase).trim();
    if (!raw) return null;
    const lower = raw.toLowerCase();
    // Magnitude-aware: "50k" -> 50000, "1.5m" -> 1500000. A bare "k" (magnitude) is meaningful; the unit
    // noise around it ("sf", "sq ft") is not, and is dropped by number extraction.
    const num = parseMagnitudeNumber(lower);

    if (isNumericType(type) || num != null) {
        if (num == null) return null;
        const hit = NUMERIC_OPS.find(o => opMatches(lower, o));
        return { operator: hit ? hit.op : "equals", value: String(num) };
    }
    // Text: pull the operator keyword, then treat the remainder as the search value.
    const hit = TEXT_OPS.find(o => opMatches(lower, o));
    const operator = hit ? hit.op : "contains";
    let value = raw;
    if (hit) {
        (hit.words || []).forEach(w => {
            value = value.replace(new RegExp(`\\b${escapeRe(w)}\\b`, "gi"), " ");
        });
    }
    value = value.replace(/\s+/g, " ").trim();
    if (!value) return null;
    return { operator, value };
};

// Read a page's registered `tableController` into the `app_context.table` payload sent on /interpret (the
// grounding surface the interpreter + proposal use). Defensive by design: a controller that is missing or
// whose getContext throws yields an EMPTY table context — Otto simply has nothing to ground against, and a
// send-time hiccup on a page never breaks the chat (best-effort, exactly like the nav/charts context).
export const buildTableContext = controller => {
    let ctx = null;
    try {
        ctx = controller && typeof controller.getContext === "function" ? controller.getContext() : null;
    } catch (e) {
        ctx = null;
    }
    return {
        columns: buildTableColumns(ctx || {}),
        sort: (ctx && ctx.sort) || null,
        pagination: (ctx && ctx.pagination) || null,
        // What THIS page's export can actually produce ({ formats, advanced }) — so the proposal grounds a
        // captured format against the page's real export UI instead of a shared assumption.
        exportOptions: exportOptionsOf(ctx && ctx.exportOptions),
        // Which view-shaping actions this page really wired (controller.availableActions()). Absent action ⇒
        // never proposed — see the TABLE_ACTIONS comment above.
        availableActions: (ctx && Array.isArray(ctx.availableActions) && ctx.availableActions) || []
    };
};
